import { Calendar, Input, Modal } from "antd";
import {
  CalendarOutlined,
  DoubleLeftOutlined,
  DoubleRightOutlined,
  LeftOutlined,
  RightOutlined,
} from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";
import { useEffect, useState } from "react";
import type { InputProps } from "antd";
import "../styles/MobileDatePicker.css";

type MobileDatePickerProps = {
  value?: Dayjs | null;
  onChange?: (date: Dayjs | null) => void;
  disabledDate?: (current: Dayjs) => boolean;
  disabled?: boolean;
  status?: InputProps["status"];
  placeholder?: string;
};

type PickerMode = "date" | "month" | "year";

export const MobileDatePicker = ({
  value,
  onChange,
  disabledDate,
  disabled = false,
  status,
  placeholder = "日付を選択",
}: MobileDatePickerProps) => {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<Dayjs | null>(value ?? null);
  const [pickerMode, setPickerMode] = useState<PickerMode>("date");

  useEffect(() => {
    if (!open) setDraft(value ?? null);
  }, [open, value]);

  const handleOpen = () => {
    if (disabled) return;
    setDraft(value ?? null);
    setPickerMode("date");
    setOpen(true);
  };

  const handleCancel = () => {
    setPickerMode("date");
    setOpen(false);
  };

  const handleOk = () => {
    onChange?.(draft);
    setPickerMode("date");
    setOpen(false);
  };

  const renderCalendarHeader = ({
    value,
    onChange,
  }: {
    value: Dayjs;
    onChange: (date: Dayjs) => void;
  }) => {
    const changePanel = (date: Dayjs) => onChange(date);

    return (
      <div className="mobile-date-picker-header">
        <button
          type="button"
          aria-label="前年"
          onClick={() => changePanel(value.subtract(1, "year"))}
        >
          <DoubleLeftOutlined />
        </button>
        <button
          type="button"
          aria-label="前月"
          onClick={() => changePanel(value.subtract(1, "month"))}
        >
          <LeftOutlined />
        </button>
        <button
          type="button"
          className={`mobile-date-picker-header-button ${pickerMode === "year" ? "is-active" : ""}`}
          aria-label="年を選択"
          onClick={() => setPickerMode("year")}
        >
          {value.year()}年
        </button>
        <button
          type="button"
          className={`mobile-date-picker-header-button ${pickerMode === "month" ? "is-active" : ""}`}
          aria-label="月を選択"
          onClick={() => setPickerMode("month")}
        >
          {value.month() + 1}月
        </button>

        <button
          type="button"
          aria-label="次月"
          onClick={() => changePanel(value.add(1, "month"))}
        >
          <RightOutlined />
        </button>
        <button
          type="button"
          aria-label="翌年"
          onClick={() => changePanel(value.add(1, "year"))}
        >
          <DoubleRightOutlined />
        </button>
      </div>
    );
  };

  const renderMonthPicker = () => (
    <div className="mobile-date-picker-options" aria-label="月を選択">
      {Array.from({ length: 12 }, (_, month) => (
        <button
          key={month}
          type="button"
          className={`mobile-date-picker-option ${draft?.month() === month ? "is-selected" : ""}`}
          onClick={() => {
            setDraft((current) => (current ?? dayjs()).month(month));
            setPickerMode("date");
          }}
        >
          {month + 1}
        </button>
      ))}
    </div>
  );

  const renderYearPicker = () => {
    const currentYear = draft?.year() ?? dayjs().year();
    const years = Array.from(
      { length: 11 },
      (_, index) => currentYear - 5 + index,
    );

    return (
      <div
        className="mobile-date-picker-options mobile-date-picker-year-options"
        aria-label="年を選択"
      >
        {years.map((year) => (
          <button
            key={year}
            type="button"
            className={`mobile-date-picker-option ${currentYear === year ? "is-selected" : ""}`}
            onClick={() => {
              setDraft((current) => (current ?? dayjs()).year(year));
              setPickerMode("date");
            }}
          >
            {year}
          </button>
        ))}
      </div>
    );
  };

  return (
    <>
      <div className="mobile-date-picker-input-wrapper" onClick={handleOpen}>
        <Input
          style={{ width: "100%", marginTop: 6 }}
          value={value?.format("YYYY/MM/DD") ?? ""}
          placeholder={placeholder}
          readOnly
          status={status}
          disabled={disabled}
          suffix={<CalendarOutlined style={{ color: "#9ca3af" }} />}
        />
      </div>
      <Modal
        open={open}
        title="日付を選択"
        centered
        destroyOnHidden
        onCancel={handleCancel}
        onOk={handleOk}
        okText="OK"
        cancelText="キャンセル"
      >
        {pickerMode === "date" ? (
          <Calendar
            fullscreen={false}
            value={draft ?? dayjs()}
            headerRender={renderCalendarHeader}
            onSelect={setDraft}
            disabledDate={disabledDate}
          />
        ) : (
          <>
            {renderCalendarHeader({
              value: draft ?? dayjs(),
              onChange: setDraft,
            })}
            {pickerMode === "month" ? renderMonthPicker() : renderYearPicker()}
          </>
        )}
      </Modal>
    </>
  );
};
